import { PublicClientApplication, type AccountInfo } from '@azure/msal-browser';

const clients = new Map<string, Promise<PublicClientApplication>>();

const getClient = (clientId: string, tenantId: string): Promise<PublicClientApplication> => {
  const key = `${clientId.toLowerCase()}:${tenantId.toLowerCase()}`;
  let pending = clients.get(key);
  if (!pending) {
    pending = (async () => {
      const client = new PublicClientApplication({
        auth: {
          clientId,
          authority: `https://login.microsoftonline.com/${encodeURIComponent(tenantId)}`,
          redirectUri: `${window.location.origin}/auth-redirect.html`,
          navigateToLoginRequestUrl: false
        },
        cache: { cacheLocation: 'sessionStorage' }
      });
      await client.initialize();
      return client;
    })();
    clients.set(key, pending);
  }
  return pending;
};

const tenantAccount = (accounts: AccountInfo[], tenantId: string): AccountInfo | undefined =>
  accounts.find(account => account.tenantId?.toLowerCase() === tenantId.toLowerCase());

export const acquireGraphFeatureTokenSilently = async (input: {
  clientId: string;
  tenantId: string;
  scopes: string[];
  loginHint?: string;
}): Promise<string | null> => {
  const client = await getClient(input.clientId, input.tenantId);
  const account = tenantAccount(client.getAllAccounts(), input.tenantId);
  try {
    const result = account
      ? await client.acquireTokenSilent({ account, scopes: input.scopes })
      : await client.ssoSilent({ scopes: input.scopes, loginHint: input.loginHint });
    return result.accessToken || null;
  } catch {
    return null;
  }
};

/** Called only after the administrator accepts the Portal's permission explanation. */
export const acquireGraphFeatureTokenInteractively = async (input: {
  clientId: string;
  tenantId: string;
  scopes: string[];
}): Promise<string> => {
  const client = await getClient(input.clientId, input.tenantId);
  const account = tenantAccount(client.getAllAccounts(), input.tenantId);
  const result = await client.acquireTokenPopup({
    scopes: input.scopes,
    ...(account ? { account } : {}),
    ...(!account ? { prompt: 'select_account' as const } : {})
  });
  if (!result.accessToken) {
    throw new Error('Microsoft did not return an access token for this optional feature.');
  }
  return result.accessToken;
};
